/**
 * 🎨 Unified Theme System - Central Registry
 * Sistema de temas unificado para toda la aplicación
 * 
 * @description Registro central de todos los temas disponibles con
 * metadata completa y configuración unificada
 */

import { googleOpalLightTheme } from './presets/google-opal-light';
import { googleOpalDarkTheme } from './presets/google-opal-dark';
import { corporateTheme } from './presets/corporate';
import { highContrastTheme } from './presets/high-contrast';
import { aiflockTheme, aiflockDarkTheme } from './presets/aiflock';

/**
 * 🎯 Registro completo de temas unificados
 * Cada tema controla toda la aplicación: Chat, Editor, Roles, Settings
 */
export const unifiedThemes = {
  // 🌟 Google Opal - Temas principales inspirados en Google (OCULTOS)
  'google-opal-light': {
    ...googleOpalLightTheme,
    id: 'google-opal-light',
    displayName: 'Google Opal Light',
    description: 'Tema claro inspirado en Google Opal con paleta moderna y profesional',
    category: 'modern',
    featured: false,
    hidden: true, // Oculto del selector de temas
    preview: {
      primary: '#1976D2',
      secondary: '#9C27B0',
      background: '#FAFBFC',
      surface: '#FFFFFF'
    }
  },

  'google-opal-dark': {
    ...googleOpalDarkTheme,
    id: 'google-opal-dark',
    displayName: 'Google Opal Dark',
    description: 'Tema oscuro elegante con paleta sofisticada para trabajo nocturno',
    category: 'modern',
    featured: false,
    hidden: true, // Oculto del selector de temas
    preview: {
      primary: '#60A5FA',
      secondary: '#C084FC',
      background: '#0F172A',
      surface: '#1E293B'
    }
  },

  // 🏢 Corporate - Tema empresarial (OCULTO)
  'corporate': {
    ...corporateTheme,
    id: 'corporate',
    displayName: 'Corporate Professional',
    description: 'Tema corporativo con paleta conservadora y máxima profesionalidad',
    category: 'business',
    featured: false,
    hidden: true, // Oculto del selector de temas
    preview: {
      primary: '#2563EB',
      secondary: '#64748B',
      background: '#F8FAFC',
      surface: '#FFFFFF'
    }
  },

  // ♿ High Contrast - Accesibilidad máxima
  'high-contrast': {
    ...highContrastTheme,
    id: 'high-contrast',
    displayName: 'High Contrast',
    description: 'Tema de alto contraste optimizado para accesibilidad WCAG 2.1 AAA',
    category: 'accessibility',
    featured: false,
    accessibility: true,
    preview: {
      primary: '#000000',
      secondary: '#1565C0',
      background: '#FFFFFF',
      surface: '#FFFFFF'
    }
  },

  // 🦅 AIFlock - Tema oficial inspirado en aiflock.ai
  'aiflock': {
    ...aiflockTheme,
    id: 'aiflock',
    displayName: 'AIFlock',
    description: 'Tema moderno con gradientes púrpura-magenta inspirado en aiflock.ai',
    category: 'modern',
    featured: true,
    preview: {
      primary: '#9333EA',
      secondary: '#EC4899',
      background: '#FAFAFA',
      surface: '#FFFFFF'
    }
  },

  'aiflock-dark': {
    ...aiflockDarkTheme,
    id: 'aiflock-dark',
    displayName: 'AIFlock Dark',
    description: 'Versión oscura del tema AIFlock con gradientes vibrantes',
    category: 'modern',
    featured: true,
    preview: {
      primary: '#A855F7',
      secondary: '#F472B6',
      background: '#0F0F0F',
      surface: '#1A1A1A'
    }
  }
};

/**
 * 🎯 Tema por defecto del sistema
 */
export const DEFAULT_THEME = 'aiflock';

/**
 * 📊 Metadatos de categorías de temas
 */
export const themeCategories = {
  modern: {
    name: 'Modern',
    description: 'Temas modernos con paletas contemporáneas',
    icon: '✨'
  },
  business: {
    name: 'Business',
    description: 'Temas profesionales para entornos corporativos',
    icon: '🏢'
  },
  accessibility: {
    name: 'Accessibility',
    description: 'Temas optimizados para accesibilidad',
    icon: '♿'
  }
};

/**
 * 🔍 Utilidades para trabajar con temas
 */

/**
 * Obtiene un tema por su ID
 * @param {string} themeId - ID del tema
 * @returns {Object|null} Configuración del tema
 */
export const getThemeById = (themeId) => {
  return unifiedThemes[themeId] || null;
};

/**
 * Obtiene todos los temas de una categoría
 * @param {string} category - Categoría a filtrar
 * @returns {Array} Array de temas
 */
export const getThemesByCategory = (category) => {
  return Object.values(unifiedThemes).filter(theme => theme.category === category);
};

/**
 * Obtiene todos los temas destacados
 * @returns {Array} Array de temas destacados
 */
export const getFeaturedThemes = () => {
  return Object.values(unifiedThemes).filter(theme => theme.featured);
};

/**
 * Obtiene todos los temas accesibles
 * @returns {Array} Array de temas con características de accesibilidad
 */
export const getAccessibleThemes = () => {
  return Object.values(unifiedThemes).filter(theme => theme.accessibility);
};

/**
 * Valida si un tema existe
 * @param {string} themeId - ID del tema a validar
 * @returns {boolean} True si el tema existe
 */
export const isValidTheme = (themeId) => {
  return themeId in unifiedThemes;
};

/**
 * Obtiene el tema apropiado basado en preferencias del sistema
 * @param {string} preferredTheme - Tema preferido del usuario
 * @returns {string} ID del tema a usar
 */
export const getSystemAppropriateTheme = (preferredTheme) => {
  // Si el tema preferido existe, usarlo
  if (isValidTheme(preferredTheme)) {
    return preferredTheme;
  }

  // Detectar preferencia del sistema
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  // Detectar preferencia de alto contraste
  const prefersHighContrast = window.matchMedia && window.matchMedia('(prefers-contrast: high)').matches;

  if (prefersHighContrast) {
    return 'high-contrast';
  }

  return prefersDark ? 'aiflock-dark' : 'aiflock';
};

/**
 * 🎨 Configuración global del sistema de temas
 */
export const themeSystemConfig = {
  // Transición suave entre temas
  transitionDuration: '200ms',
  transitionEasing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  
  // Persistencia
  storageKey: 'activeTheme',
  fallbackTheme: DEFAULT_THEME,
  
  // Características
  supportsCSSVariables: true,
  supportsSystemTheme: true,
  supportsHighContrast: true,
  
  // Debugging
  enableThemeDebugging: process.env.NODE_ENV === 'development'
};

console.log(`🎨 [ThemeSystem] Inicializado con ${Object.keys(unifiedThemes).length} temas disponibles`);

export default unifiedThemes;
